import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { entityService } from '../../../services/entityService';

export const wordImportCommand = {
  id: 'word_import',
  description: '📝 IMPORT WORD: Convert a Word document (.docx, .doc, .rtf) to an editable River document',
  logicalName: 'word_import',
  version: 'v1',
  appType: 'docs',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  examples: {
    good: [
      {
        input: { entityId: '507f1f77bcf86cd799439011' },
        expectedOutput: {
          status: 'success',
          message: 'Successfully imported Word document',
          format: 'json',
          data: { type: 'word_imported', entityId: '507f1f77bcf86cd799439011' }
        } as StandardToolResult,
        description: 'Import an unimported Word document',
        context: 'When Word doc is attached to context but not imported'
      }
    ],
    bad: [
      {
        input: { entityId: '' },
        error: 'Missing entity ID',
        why: 'entityId parameter is required',
        fix: 'Provide valid Word file entity ID'
      }
    ]
  },
  
  performance: {
    expectedDuration: 5000, // 5 seconds average
    complexity: 'medium',
    memoryIntensive: false
  },
  
  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'Word file entity ID (_id)',
        required: true,
      },
      {
        name: 'llmId',
        type: 'string',
        description: 'Word file LLM ID',
        required: false,
      },
      {
        name: 'mode',
        type: 'string',
        description: 'Import mode: standard (default), plaintext, or smart',
        required: false,
      }
    ]
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      const { entityId, mode = 'standard' } = params;
      
      if (!entityId) {
        return createErrorResult('Missing entity ID', 'entityId parameter is required', 'validation');
      }

      // Fetch the Word file entity
      let wordEntity;
      try {
        wordEntity = await entityService.fetchEntity(entityId);
      } catch (fetchError) {
        return createErrorResult(
          'Word file not found',
          `Could not find Word file entity with ID "${entityId}"`,
          'validation'
        );
      }

      const fileName = wordEntity?.name || wordEntity?.skeleton?.fileName || 'Document';
      const isRtf = fileName.toLowerCase().endsWith('.rtf');
      
      // Check if already imported
      if (wordEntity?.skeleton?.importedDocId) {
        return createSuccessResult(
          `Word document "${fileName}" is already imported`,
          {
            type: 'word_already_imported',
            entityId,
            importedDocId: wordEntity.skeleton.importedDocId,
            message: `This Word document has already been converted to an editable document.`,
            metadata: {
              executionTime: Date.now() - startTime
            }
          },
          {
            format: 'json',
            metadata: {
              executionTime: Date.now() - startTime,
              method: 'word_import_skipped'
            } as any
          }
        );
      }

      context.onProgress?.({ 
        type: 'streaming_update', 
        activity: `Importing Word document "${fileName}"...` 
      });

      // Get file path from entity
      const filePath = wordEntity.skeleton?.path;
      
      if (!filePath) {
        return createErrorResult(
          'Word file has no path',
          'The Word file entity does not have a storage path',
          'validation'
        );
      }

      context.onProgress?.({ 
        type: 'streaming_update', 
        activity: `Extracting text from "${fileName}"...` 
      });

      const { API_BASE_URL } = await import('../../../api/config');
      const fetchService = (await import('../../../services/fetchService')).default;

      let docName = fileName.replace(/\.(docx?|rtf)$/i, '');
      let docSkeleton: any;

      if (isRtf) {
        // RTF → HTML via pandoc, then store as a y.js editable doc (same as DOCX interactive path)
        context.onProgress?.({ type: 'streaming_update', activity: `Converting RTF "${fileName}"...` });

        let rtfData;
        try {
          rtfData = await fetchService(`${API_BASE_URL}/files/rtf/extract-html?path=${encodeURIComponent(filePath)}`);
        } catch (error) {
          return createErrorResult(
            'RTF conversion failed',
            `Could not convert RTF file: ${error instanceof Error ? error.message : 'Unknown error'}`,
            'execution'
          );
        }

        if (!rtfData.success || !rtfData.html) {
          return createErrorResult('Empty document', 'RTF document appears to be empty or could not be read', 'execution');
        }

        docSkeleton = {
          contentFormat: 'y.js',
          content: rtfData.html,
          importedFrom: 'rtf',
          originalFileName: fileName,
          originalFileId: entityId,
          wordCount: rtfData.wordCount,
        };
      } else {
        // DOCX/DOC → plain text + simple_nodes (existing behaviour)
        context.onProgress?.({ type: 'streaming_update', activity: `Extracting text from "${fileName}"...` });

        let extractedData;
        try {
          extractedData = await fetchService(`${API_BASE_URL}/files/word/extract-text?path=${encodeURIComponent(filePath)}`);
        } catch (error) {
          return createErrorResult(
            'Text extraction failed',
            `Could not extract text from Word file: ${error instanceof Error ? error.message : 'Unknown error'}`,
            'execution'
          );
        }

        if (!extractedData.success || !extractedData.text) {
          return createErrorResult('Empty document', 'Word document appears to be empty or could not be read', 'execution');
        }

        const markdown = extractedData.paragraphs.map((p: string) => p).join('\n\n');
        docSkeleton = {
          contentFormat: 'simple_nodes',
          content: markdown || '<p></p>',
          wordAsDocument: extractedData.wordAsDocument,
          importedFrom: 'word',
          originalFileName: fileName,
          originalFileId: entityId,
          wordCount: extractedData.wordCount,
          paragraphCount: extractedData.paragraphCount,
        };
      }

      context.onProgress?.({ 
        type: 'streaming_update', 
        activity: `Creating document from "${fileName}"...` 
      });

      // Get space ID from context or entity
      const spaceId = context.spaceId || (wordEntity as any).spaceId || (wordEntity.skeleton as any)?.spaceId;
      if (!spaceId) {
        return createErrorResult(
          'Missing space ID',
          'Could not determine space ID. The Word file must be in a space to import.',
          'validation'
        );
      }

      const docEntity: any = await entityService.create({
        entityType: 'Doc',
        name: docName,
        spaceId,
        skeleton: docSkeleton,
      });

      if (!docEntity?.llmId) {
        return createErrorResult(
          'Document creation failed',
          'Created document is missing llmId',
          'execution'
        );
      }

      const importResult = {
        docEntity,
        metadata: {
          wordCount: docSkeleton.wordCount || 0,
          paragraphCount: docSkeleton.paragraphCount || 0,
          hasImages: false
        }
      };

      console.log('[word_import] Import successful:', {
        importedDocId: importResult.docEntity._id,
        importedDocLlmId: importResult.docEntity.llmId,
        wordCount: importResult.metadata.wordCount
      });

      // Update the original Word entity to reference the imported doc
      await entityService.mergeUpdate(entityId, {
        skeleton: {
          importedDocId: importResult.docEntity._id,
          importedDocLlmId: importResult.docEntity.llmId,
          importedAt: new Date().toISOString()
        }
      });

      return createSuccessResult(
        `Successfully imported "${fileName}"`,
        {
          type: 'word_imported',
          originalEntityId: entityId,
          importedDocId: importResult.docEntity._id,
          importedDocLlmId: importResult.docEntity.llmId,
          wordCount: importResult.metadata.wordCount,
          message: `Word document has been converted to an editable document. You can now read it with doc_read_v2 using llmId: ${importResult.docEntity.llmId}`,
          metadata: {
            executionTime: Date.now() - startTime,
            wordCount: importResult.metadata.wordCount,
            hasImages: importResult.metadata.hasImages
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'word_import_completed'
          } as any
        }
      );
      
    } catch (error) {
      return createErrorResult(
        'Import error',
        error instanceof Error ? error.message : 'Unknown error during Word import',
        'execution',
        {
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    }
  }
};

